import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Flex from '../../../components/base/Flex';
import KeyboardToggleSvg from '../../../components/svg/KeyboardToggleSvg';
import ShakeSvg from '../../../components/svg/ShakeSvg';
import TextButton from '../../../components/base/TextButton';
import Biometrics from './widgets/Biometrics';
import jsFileDownload from 'js-file-download';
import OpenUrl from './widgets/OpenUrl';
import TypeText from './widgets/TypeText';
import SelectKey from './widgets/SelectKey';
import { useState } from 'react';
import { getCurrentDate } from '../../SuitesRuns/utils';
import { toast } from 'react-toastify';
import { useTheme } from '@emotion/react';
import { v4 } from 'uuid';
import { useActiveProject } from '../../../store/projectState';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import CachedIcon from '@mui/icons-material/Cached';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';

const WidgetsPro = ({
  orientation,
  setOrientation,
  session,
  platform,
  osVersion,
  onInspect,
  setUnsaved,
  setActions,
  selectedElement,
  assertionMode,
  setAssertionMode,
  assertionModeRef,
  toggleAssertionModeRef,
  insertEventsAtRef,
  startRecordingRef,
  handleToggleDebugMode,
  debugMode,
  sessionActive
}) => {
  const activeProject = useActiveProject();
  const [direction, setDirection] = useState('right');
  const theme = useTheme();
  const isRecordingMode = startRecordingRef?.current;

  const toggleDirection = async () => {
    setDirection((prevDirection) => (prevDirection === 'left' ? 'right' : 'left'));
    await session.rotate(direction);
  };

  const toggleAssertionMode = async () => {
    setAssertionMode((prevState) => !prevState);
    assertionModeRef.current = !assertionModeRef.current;

    toast.info(
      assertionMode
        ? "You're back to recording mode"
        : 'Your are now in assertion mode, click on an element on the emulator to assert against',
      {
        autoClose: 7000
      }
    );
  };

  const format = 'png';
  const isIOS = platform === 'ios';

  const handleScreenshot = async () => {
    const { data } = await session.screenshot();
    jsFileDownload(data, `${getCurrentDate()} High Resolution Screenshot .${format}`);
  };

  const handleDeviceShake = async () => {
    await session.shake();
  };

  const handleToggleKeyboard = async () => {
    await session.toggleSoftKeyboard();
  };

  const handleOpenScandiumMailBox = async () => {
    const isProduction = window.location.href.includes('app');
    const mailBoxAddress = `https://${
      isProduction ? 'app' : 'playground'
    }.getscandium.com/projects/${activeProject?.id}/mailbox`;

    const data = {
      type: 'openUrl',
      url: mailBoxAddress,
      id: v4(),
      timeout: 15000,
      step_delay: 6000
    };

    try {
      await session.openUrl(mailBoxAddress);

      if (insertEventsAtRef.current !== null) {
        const insertIndex = insertEventsAtRef.current + 1;
        setActions((prevActions) => [
          ...prevActions.slice(0, insertIndex),
          { ...data },
          ...prevActions.slice(insertIndex)
        ]);
        insertEventsAtRef.current = insertIndex;
      } else {
        setActions((prevActions) => [...prevActions, { ...data }]);
      }

      setUnsaved(true);
    } catch (e) {
      console.error('Failed to open mailbox', e);
    }
  };

  const handleToggleDebugModeConfirmation = () => {
    if (sessionActive) {
      const confirmChange = window.confirm('This will end your current session. Are you sure?');
      if (confirmChange) {
        handleToggleDebugMode();
      }
    } else {
      handleToggleDebugMode();
    }
  };

  return (
    <Grid
      container
      sx={{
        width: '100%',
        borderRadius: '0.3rem'
      }}>
      <Grid size={12} item>
        <TextButton sx={{ py: 1 }} onClick={onInspect}>
          <Flex sx={{ flexDirection: 'column' }}>
            <ContentPasteIcon sx={{ color: theme.palette.text.lightGrey_dark }} />
            <Typography
              variant={'body2'}
              color={theme.palette.text.black_grey}
              fontWeight={500}
              fontSize={'0.85rem'}>
              Inspect
            </Typography>
          </Flex>
        </TextButton>
      </Grid>
      {((platform === 'android' && +osVersion >= 8) || platform === 'ios') && (
        <Grid size={12} item>
          <Biometrics
            orientation={orientation}
            setUnsaved={setUnsaved}
            setActions={setActions}
            session={session}
            insertEventsAtRef={insertEventsAtRef}
            theme={theme}
            isRecordingMode={isRecordingMode}
          />
        </Grid>
      )}
      <Grid size={12} item>
        <TextButton sx={{ py: 1 }} disabled={!session} onClick={handleScreenshot}>
          <Flex sx={{ flexDirection: 'column' }}>
            <InsertPhotoOutlinedIcon sx={{ color: theme.palette.svg.primary }} />
            <Typography
              variant={'body2'}
              color={theme.palette.text.black_grey}
              fontWeight={500}
              fontSize={'0.85rem'}>
              Screenshot
            </Typography>
          </Flex>
        </TextButton>
      </Grid>
      <Grid size={12} item>
        <TextButton sx={{ py: 1 }} onClick={toggleDirection}>
          <Flex sx={{ flexDirection: 'column' }}>
            <CachedIcon sx={{ color: theme.palette.svg.primary }} />
            <Typography
              variant={'body2'}
              color={theme.palette.text.black_grey}
              fontWeight={500}
              fontSize={'0.85rem'}>
              Rotate
            </Typography>
          </Flex>
        </TextButton>
      </Grid>
      <Grid size={12} item>
        <OpenUrl
          orientation={orientation}
          session={session}
          theme={theme}
          isRecordingMode={isRecordingMode}
          setUnsaved={setUnsaved}
          setActions={setActions}
          insertEventsAtRef={insertEventsAtRef}
        />
      </Grid>
      <Grid size={12} item>
        <TypeText
          orientation={orientation}
          session={session}
          theme={theme}
          isRecordingMode={isRecordingMode}
        />
      </Grid>
      <Grid size={12} item>
        <SelectKey
          orientation={orientation}
          session={session}
          platform={platform}
          theme={theme}
          isRecordingMode={isRecordingMode}
        />
      </Grid>
      <Grid size={12} item>
        <Tooltip title={'Click to toggle assertion mode'}>
          <TextButton
            sx={{ py: 1 }}
            onClick={toggleAssertionMode}
            disabled={!session || !isRecordingMode}
            aria-controls="simple-menu"
            aria-haspopup="true">
            <Flex sx={{ flexDirection: 'column' }}>
              <CheckCircleOutlineIcon sx={{ color: theme.palette.svg.primary }} />
              <Typography
                variant={'body2'}
                color={assertionMode ? 'green' : theme.palette.text.black_grey}
                fontWeight={500}
                fontSize={'0.85rem'}>
                Assertion
              </Typography>
            </Flex>
          </TextButton>
        </Tooltip>
      </Grid>
      {isIOS && (
        <>
          <Grid size={12} item>
            <TextButton sx={{ py: 1 }} disabled={!session} onClick={handleToggleKeyboard}>
              <Flex sx={{ flexDirection: 'column' }}>
                <KeyboardToggleSvg fill={theme.palette.svg.primary} />
                <Typography
                  variant={'body2'}
                  textAlign={'center'}
                  color={theme.palette.text.black_grey}
                  fontWeight={500}
                  fontSize={'0.85rem'}>
                  Keyboard <br />
                  Toggle
                </Typography>
              </Flex>
            </TextButton>
          </Grid>
          <Grid size={12} item>
            <TextButton sx={{ py: 1 }} disabled={!session} onClick={handleDeviceShake}>
              <Flex sx={{ flexDirection: 'column' }}>
                <ShakeSvg fill={theme.palette.svg.primary} />
                <Typography
                  variant={'body2'}
                  color={theme.palette.text.black_grey}
                  fontWeight={500}
                  fontSize={'0.85rem'}>
                  Shake
                </Typography>
              </Flex>
            </TextButton>
          </Grid>
        </>
      )}

      <Grid size={12} item>
        <TextButton
          sx={{ py: 1 }}
          disabled={!session || !isRecordingMode}
          onClick={handleOpenScandiumMailBox}>
          <Flex sx={{ flexDirection: 'column' }}>
            <EmailOutlinedIcon sx={{ color: theme.palette.svg.primary }} />
            <Typography
              variant={'body2'}
              textAlign={'center'}
              color={theme.palette.text.black_grey}
              fontWeight={500}
              fontSize={'0.85rem'}>
              Open <br />
              Mailbox
            </Typography>
          </Flex>
        </TextButton>
      </Grid>

      <Grid size={12} item>
        <TextButton sx={{ py: 1 }} title={'Toggle Debug Mode'} onClick={handleToggleDebugModeConfirmation}>
          <Flex sx={{ flexDirection: 'column' }}>
            <BugReportOutlinedIcon
              sx={{ color: theme.palette.svg.primary }}
            />
            <Typography
              variant={'body2'}
              color={theme.palette.text.black_grey}
              fontWeight={500}
              fontSize={'0.85rem'}>
              Debug ({debugMode ? 'On' : 'Off'})
            </Typography>
          </Flex>
        </TextButton>
      </Grid>
    </Grid>
  );
};

export default WidgetsPro;
